import { Inject, Injectable, inject } from '@angular/core';
import {
  Attributes,
  Context,
  Experiment,
  GrowthBook,
  Result,
} from '@growthbook/growthbook';
import { EnvironmentService } from '../environment-service/environment.service';
import { AppFeatures, GrowthBookFeatureKeys } from '@models/growthbook.models';
import { WINDOW } from '@constants/global-objects.constants';
import { Observable, Subject, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectShouldUseMobileStyling } from '@selectors/app.selector';
import {
  CULTURE_ROUTE_PARAM,
  ECONOMY_CABIN,
  GROWTHBOOK_OPTION_KEY,
  PREMIUM_CABIN,
} from '@constants/app.constants';
import {
  selectIsBookPath,
  selectIsCancelPath,
  selectIsCheckInPath,
  selectIsModifyPath,
  selectIsUpgradePath,
  selectRouteParam,
} from '@selectors/router.selector';
import {
  selectAvailabilitySelectedFaresState,
  selectAvailabilitySelectedJourneysState,
  selectAvailabilitySelectedTripsState,
  selectAvailabilityTripTypeState,
  selectTripOrigin,
  selectTripDestination,
  selectAvailabilityDisplayViewState,
  selectSearchInputCriteriaState,
  selectAvailabilitySearchInputCriteriaState,
} from '@selectors/availability.selector';
import {
  selectBooking,
  selectJourneyDetails,
} from '@selectors/booking.selector';
import { selectSelectedCurrencyCode } from '@selectors/culture.selector';
import { selectLoggedInUser } from '@selectors/user.selector';
import { HotjarService } from '@services/hotjar/hotjar.service';
import { RudderStackService } from '@services/rudderstack/rudderstack.service';
import { AppInsightsService } from '@services/app-insights/app-insights.service';
import { growthbookFeatureLoaded } from '@actions/growthbook.action';
import { BBoxService } from '@services/bbox/bbox.service';
import { BBoxEventProperties } from 'src/app/typings/bbox';
import posthog from 'posthog-js';
import { DateTime } from 'luxon';
import { DATE_FORMAT_YEAR_MONTH } from '@constants/date-format.constants';
import { SessionStorageService } from '@services/session-storage/session-storage.service';

@Injectable({
  providedIn: 'root',
})
export class GrowthBookService {
  private _growthBook: GrowthBook<AppFeatures>;
  private _chgSubject = new Subject<void>();
  private _featureLoadedSubject = new Subject<void>();
  private _isFeaturesLoaded = false;

  onChange$: Observable<void>;
  featureLoaded$ = this._featureLoadedSubject.asObservable();
  mobileStyle$ = this.store.select(selectShouldUseMobileStyling);
  culture$ = this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM));
  tripType$ = this.store.select(selectAvailabilityTripTypeState);
  sellKeys$ = this.store.select(selectAvailabilitySelectedTripsState);
  booking$ = this.store.select(selectBooking);
  selectedFares$ = this.store.select(selectAvailabilitySelectedFaresState);
  selectedJourneys$ = this.store.select(
    selectAvailabilitySelectedJourneysState,
  );
  journeyDetails$ = this.store.select(selectJourneyDetails);
  selectedCurrency$ = this.store.select(selectSelectedCurrencyCode);
  isBookPath$ = this.store.select(selectIsBookPath);
  isModifyPath$ = this.store.select(selectIsModifyPath);
  isUpgradePath$ = this.store.select(selectIsUpgradePath);
  isCancelPath$ = this.store.select(selectIsCancelPath);
  isCheckInPath$ = this.store.select(selectIsCheckInPath);
  loggedInUser$ = this.store.select(selectLoggedInUser);
  tripOrigin$ = this.store.select(selectTripOrigin);
  tripDestination$ = this.store.select(selectTripDestination);
  searchInputCriteria$ = this.store.select(selectSearchInputCriteriaState);
  searchAvailabilityInputCriteria$ = this.store.select(
    selectAvailabilitySearchInputCriteriaState,
  );
  flightView$ = this.store.select(selectAvailabilityDisplayViewState);

  private hotjarService = inject(HotjarService);
  private appInsightsService = inject(AppInsightsService);
  private bboxService = inject(BBoxService);
  private sessionStorageService = inject(SessionStorageService);

  constructor(
    @Inject(WINDOW) private readonly window: Window,
    private rudderStackService: RudderStackService,
    public store: Store,
  ) {
    const defaultOptions = this.getDefaultOptions();
    const queryOptions = this.fetchQueryOptions(true);
    const sessionOptions = this.fetchSessionOptions();
    const context = {
      ...defaultOptions,
      ...sessionOptions,
      ...queryOptions,
    } as Context;

    this._growthBook = new GrowthBook<AppFeatures>(context);

    this.bboxService.onReady$.pipe(take(1)).subscribe({
      complete: () => {
        this._growthBook.setTrackingCallback((experiment, result) =>
          this.trackingCallback(experiment, result),
        );
      },
    });

    this.rudderStackService.onReady$.subscribe(() => {
      this.addAttribute('id', this.rudderStackService.getAnonymousId());
    });

    this.onChange$ = this._chgSubject.asObservable();
    this.growthBook.setRenderer(() => this._chgSubject.next());

    this.createAttributeSubscriptions();
  }

  get growthBook(): GrowthBook<AppFeatures> {
    return this._growthBook;
  }

  private getDefaultOptions(): Context {
    const options: Context = {} as Context;
    options.apiHost = EnvironmentService.environment.growthBook.apiHost;
    options.clientKey = EnvironmentService.environment.growthBook.clientKey;
    options.enableDevMode =
      EnvironmentService.environment.growthBook.enableDevMode === 'true';
    options.attributes = this.getDefaultAttributes();
    return options;
  }

  private getDefaultAttributes(): Attributes {
    return {
      id: this.rudderStackService.getAnonymousId(),
      isBot: false,
    } as Attributes;
  }

  addAttribute(key: string, value: unknown): void {
    this.growthBook.setAttributes({
      ...this.growthBook.getAttributes(),
      ...{ [key]: value },
    });
  }

  async loadFeatures(): Promise<void> {
    const response = await this.growthBook.init({
      skipCache: this.debug,
      timeout: 5000,
      streaming: true,
    });
    if (response.success) {
      this._isFeaturesLoaded = true;
      this.store.dispatch(growthbookFeatureLoaded());
      this._featureLoadedSubject.next();
    }
    if (response.source) {
      this.log('GrowthBook: Features loaded from ', response.source);
    }
    if (response.error) {
      console.error('error_loading_gb', response.error);
    }
  }

  trackingCallback(experiment: Experiment<unknown>, result: Result<unknown>) {
    const eventProperties: BBoxEventProperties = {
      experimentId: experiment.key,
      variationId: result.key,
    };

    this.log('GrowthBook: Trigger Experiment Viewed', {
      experiment: experiment,
      result: result,
    });
    this.bboxService.trackExperimentViewed(eventProperties);

    if (this.hotjarService.isEnabled()) {
      this.trackHotjarEvent(experiment.key, result);
    }

    this.trackEvent(experiment.key, result.key);
  }

  /**
   * @deprecated use growthBook.isOn instead
   */
  isOn(feature: GrowthBookFeatureKeys): boolean {
    const isOn = this.growthBook.isOn(feature);
    return isOn;
  }

  /**
   * @deprecated use growthBook.getFeatureValue instead
   **/
  getFeatureStringValue(
    feature: GrowthBookFeatureKeys,
    defaultValue?: string,
  ): string {
    const defaultVal = defaultValue ?? '';
    const featureValue = this.growthBook.getFeatureValue(feature, defaultVal);
    return featureValue;
  }

  createAttributeSubscriptions(): void {
    this.mobileStyle$.subscribe((mobileStyle) => {
      this.updateGrowthBookAttribute('mobile', mobileStyle);
    });

    this.culture$.subscribe((culture) => {
      this.updateGrowthBookAttribute('culture', culture);
    });

    this.selectedCurrency$.subscribe((selectedCurrency) => {
      this.updateGrowthBookAttribute('currency', selectedCurrency);
    });

    this.tripType$.subscribe((tripType) => {
      this.updateGrowthBookAttribute('tripType', tripType);
    });

    this.booking$.subscribe((booking) => {
      let hasEconomy = false;
      let hasPremium = false;
      let totalAmount = 0;

      if (booking) {
        totalAmount = booking.breakdown.totalAmount ?? 0;
        hasEconomy = booking.journeys.some(
          (journey) => journey.cabin === ECONOMY_CABIN,
        );
        hasPremium = booking.journeys.some(
          (journey) => journey.cabin === PREMIUM_CABIN,
        );
      }

      this.updateGrowthBookAttributes({
        booking: !!booking,
        totalAmount,
        hasEconomy,
        hasPremium,
      });
    });

    this.isBookPath$.subscribe((isBookPath) => {
      this.updateGrowthBookAttribute('bookPath', isBookPath);
    });

    this.isModifyPath$.subscribe((isModifyPath) => {
      this.updateGrowthBookAttribute('modifyPath', isModifyPath);
    });

    this.isUpgradePath$.subscribe((isUpgradePath) => {
      this.updateGrowthBookAttribute('upgradePath', isUpgradePath);
    });

    this.isCancelPath$.subscribe((isCancelPath) => {
      this.updateGrowthBookAttribute('cancelPath', isCancelPath);
    });

    this.loggedInUser$.subscribe((loggedInUser) => {
      this.updateGrowthBookAttribute('loggedIn', !!loggedInUser);
    });

    this.tripOrigin$.subscribe((tripOrigin) => {
      this.updateGrowthBookAttribute('tripOrigin', tripOrigin);
    });

    this.tripDestination$.subscribe((tripDestination) => {
      this.updateGrowthBookAttribute('tripDestination', tripDestination);
    });

    this.isCheckInPath$.subscribe((isCheckInPath) => {
      this.updateGrowthBookAttribute('checkInPath', isCheckInPath);
    });

    this.searchAvailabilityInputCriteria$.subscribe(
      (searchAvailabilityInputCriteria) => {
        let tripOutboundAvailableDate = '';
        let tripReturnAvailableDate = '';

        if (searchAvailabilityInputCriteria) {
          tripOutboundAvailableDate =
            searchAvailabilityInputCriteria[0]?.beginDate ?? '';
          tripReturnAvailableDate =
            searchAvailabilityInputCriteria[1]?.beginDate ?? '';
        }

        this.updateGrowthBookAttributes({
          tripOutboundDate: tripOutboundAvailableDate,
          tripReturnDate: tripReturnAvailableDate,
        });
      },
    );

    this.searchInputCriteria$.subscribe((searchInputCriteria) => {
      let tripOutboundMonth = '';
      let tripReturnMonth = '';

      if (searchInputCriteria) {
        const tripOutboundDate = searchInputCriteria[0]?.beginDate ?? '';
        const tripReturnDate = searchInputCriteria[1]?.beginDate ?? '';

        const tripOutboundDateObj = DateTime.fromISO(tripOutboundDate);
        const tripReturnDateObj = DateTime.fromISO(tripReturnDate);

        tripOutboundMonth = tripOutboundDateObj.toFormat(
          DATE_FORMAT_YEAR_MONTH,
        );
        tripReturnMonth = tripReturnDateObj.toFormat(DATE_FORMAT_YEAR_MONTH);
      }

      this.updateGrowthBookAttributes({
        tripOutboundMonth,
        tripReturnMonth,
      });
    });

    this.flightView$.subscribe((flightView) => {
      this.updateGrowthBookAttribute('flightView', flightView);
    });
  }

  private updateGrowthBookAttribute(key: string, value: unknown): void {
    const currentAttributes = this.growthBook.getAttributes();

    if (currentAttributes[key] !== value) {
      this.growthBook.setAttributes({
        ...currentAttributes,
        [key]: value,
      });

      this.updateTrackingProperties();
    }
  }

  private updateGrowthBookAttributes(
    attributes: Record<string, unknown>,
  ): void {
    const currentAttributes = this.growthBook.getAttributes();
    const updatedAttributes: Record<string, unknown> = {};

    let hasChanges = false;
    for (const [key, value] of Object.entries(attributes)) {
      if (currentAttributes[key] !== value) {
        updatedAttributes[key] = value;
        hasChanges = true;
      }
    }

    if (hasChanges) {
      this.growthBook.setAttributes({
        ...currentAttributes,
        ...updatedAttributes,
      });

      this.updateTrackingProperties();
    }
  }

  private updateTrackingProperties(): void {
    const props = this.growthBook.getAttributes();
    props['last_updated'] = new Date().toISOString();
    this.bboxService.addTrackingProperties(props);
  }

  log(msg: string, data?: unknown): void {
    if (this.debug) {
      console.log(msg, data);
    }
  }

  isFeatureEnabled<K extends keyof AppFeatures>(
    experimentKey: K,
    defaultValue?: boolean,
  ): boolean {
    if (!this._isFeaturesLoaded) {
      return defaultValue ?? false;
    }
    return this._growthBook.isOn(experimentKey);
  }

  isVariantOfExperimentEnabled<
    V extends AppFeatures[K],
    K extends keyof AppFeatures,
  >(experimentKey: K, variant: V, defaultValue?: V): boolean {
    const defaultVal = defaultValue ?? this.getDefaultValueByType(variant);
    return (
      this._growthBook.isOn(experimentKey) &&
      this._growthBook.getFeatureValue(experimentKey, defaultVal) === variant
    );
  }

  getFeatureVaule<V extends AppFeatures[K], K extends keyof AppFeatures>(
    experimentKey: K,
    defaultValue: V,
  ) {
    return this._growthBook.getFeatureValue(experimentKey, defaultValue);
  }

  getFeatureVariant<V extends AppFeatures[K], K extends keyof AppFeatures>(
    experimentKey: K,
    defaultValue: V,
  ) {
    if (this._growthBook.isOff(experimentKey)) {
      return defaultValue;
    }
    return this._growthBook.getFeatureValue(experimentKey, defaultValue);
  }

  get debug(): boolean {
    return !!EnvironmentService.environment.growthBook.debug;
  }

  private trackEvent(experimentId: string, variationId: string): void {
    posthog.capture('experiment_viewed', {
      message: `Experiment Viewed: ${experimentId} - Variation ID: ${variationId}`,
      category: 'growthbook',
      data: {
        experimentId,
        variationId,
      },
    });
  }

  private trackHotjarEvent<T>(experimentId: string, result: Result<T>): void {
    if (this.hotjarService.isEnabled()) {
      const value = result.value;

      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        this.hotjarService.event(`${experimentId}_variant_${value}_displayed`);
      } else {
        this.hotjarService.event(`${experimentId}_key_${result.key}_displayed`);
      }
    }
  }

  private getDefaultValueByType<V>(value: V): V {
    if (typeof value === 'string') {
      return '' as unknown as V;
    } else if (typeof value === 'number') {
      return 0 as unknown as V;
    } else if (typeof value === 'boolean') {
      return false as unknown as V;
    } else if (typeof value === 'object' && value !== null) {
      return {} as unknown as V;
    } else {
      return null as unknown as V;
    }
  }

  private fetchQueryOptions(saveInSession: boolean = false): Context | null {
    const url = this.window.location.href;
    if (url) {
      try {
        const queryOptions = new URL(url).searchParams.get(
          GROWTHBOOK_OPTION_KEY,
        );
        if (queryOptions) {
          const parsedOptions = JSON.parse(queryOptions) as Context;
          if (saveInSession) {
            this.storeSessionOptions(parsedOptions);
          }
          return parsedOptions;
        }
      } catch (error) {
        console.error('Error parsing query options', error);
      }
    }
    return null;
  }

  private fetchSessionOptions(): Context | null {
    const sessionOptions = this.sessionStorageService.getItem<Context>(
      GROWTHBOOK_OPTION_KEY,
    );
    if (sessionOptions) {
      return sessionOptions;
    }
    return null;
  }

  private storeSessionOptions(options: Context) {
    this.sessionStorageService.setItem<Context>(GROWTHBOOK_OPTION_KEY, options);
  }
}
